import * as React from 'react';
import {Fragment, useEffect, useState} from 'react';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import {Alert, Avatar, Box, FormControl, Input, InputLabel, Link, Paper} from "@mui/material";
import {Link as RouterLink, useNavigate, useSearchParams} from "react-router-dom";
import {LockOutlined, Visibility, VisibilityOff} from "@mui/icons-material";
import {avatarStyle, boxStyle, formStyle, paperSignStyle, submitStyle, typoStyle} from "./style"
import {loginAsync, selectLoginError, selectStatus, usernameFilled} from "./sessionSlice";
import {useAppDispatch} from "../../app/hooks";
import {useSelector} from "react-redux";
import {useSnackbar} from "notistack";
import { useTranslation } from "react-i18next";
import LanguageSwitcher from '../../app/language-switcher/LanguageSwitcher';

export const Login = () => {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const [searchParams] = useSearchParams();
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [passwordStep, setPasswordStep] = useState(false);
    const [viewPassword, setViewPassword] = useState(false);
    const { enqueueSnackbar } = useSnackbar();
    const loginStatus = useSelector(selectStatus);
    const loginError = useSelector(selectLoginError);
    const dispatch = useAppDispatch();
    const updateEmail = (event: { target: { value: React.SetStateAction<string>; }; }) => {
        setEmail(event.target.value);
    };
    const updatePassword = (event: { target: { value: React.SetStateAction<string>; }; }) => {
        setPassword(event.target.value);
    };
    const handleSubmit = (event: { preventDefault: () => void; }) => {
        event.preventDefault()
        if (!passwordStep) {
            const partnerEmailDomains = (process.env.REACT_APP_PARTNER_IDP_EMAIL_DOMAIN || '')
                .split(',')
                .map((domain) => domain.trim().toLowerCase())
                .filter(Boolean);
            const emailRegex = new RegExp(
              `^[^@]+@[^@]+\\.(${partnerEmailDomains.map(domain => domain.replace(/\./g, '\\.')).join('|')})$`,
            );
            if (email && emailRegex.test(email)) {
                window.location.href = `${process.env.REACT_APP_API_URL}/session/sso/start?email=${encodeURIComponent(email.trim())}`;
                return;
            }

            dispatch(usernameFilled(email.trim()));
            setPasswordStep(true);
            return;
        }

        dispatch(loginAsync({username: email, password}))
    }
    const togglePasswordVisibility = () => {
        setViewPassword(!viewPassword)
    }
    const changeEmail = () => {
        setPassword('');
        setPasswordStep(false);
    }
    useEffect(()=> {
        if(loginStatus === 'login-rejected'){
            if (loginError === 'MIGRATION_UPDATE_ERROR') {
                navigate('/reinit-1');
                return;
            }
            if (loginError === 'UPDATE_PASSWORD_REQUIRED') {
                navigate('/init');
                return;
            }
            enqueueSnackbar(t('snack.invalidUserOrPassword'), {
                autoHideDuration: 5000,
                variant: 'warning'
            })
        }
    },[enqueueSnackbar, loginStatus, loginError, navigate, t])
    return (
        <Box sx={boxStyle}>
            {searchParams.get("reason") &&
            searchParams.get("reason") === "disconnect" && (
                <Alert
                severity="warning"
                style={{
                    marginTop: "16px",
                }}
                >
                {t('loggedOutPleaseLogin')}
                </Alert>
            )}
            <Paper className="paperSign" sx={paperSignStyle}>
                <div style={{position: "relative", top: "-5%", right: "-45%"}}>
                    <LanguageSwitcher />
                </div>
                <Avatar className="avatar" sx={avatarStyle}>
                    <LockOutlined/>
                </Avatar>
                <Fragment>
                    <Typography component="h1" variant="h5">
                        {t('loginTitle')}
                    </Typography>
                    <form className="Form" onSubmit={handleSubmit}>
                        {!passwordStep ? (
                            <FormControl margin="normal" required fullWidth sx={formStyle}>
                                <InputLabel htmlFor="email">{t('email')}</InputLabel>
                                <Input id="email"
                                       name="email"
                                       type="email"
                                       autoComplete="email"
                                       autoFocus
                                       value={email}
                                       onChange={updateEmail}/>
                            </FormControl>
                        ) : (
                            <>
                                <FormControl margin="normal" required fullWidth sx={formStyle}>
                                    <InputLabel htmlFor="email">{t('email')}</InputLabel>
                                    <Input id="email"
                                           name="email"
                                           type="email"
                                           autoComplete="username"
                                           value={email}
                                           inputProps={{readOnly: true}}/>
                                </FormControl>
                                <FormControl margin="normal" required fullWidth sx={formStyle}
                                    style={{position: 'relative'}}>
                                    <InputLabel htmlFor="password">{t('password')}</InputLabel>
                                    <Input name="password"
                                           type={viewPassword ? 'text' : 'password'}
                                           id="password"
                                           autoComplete="current-password"
                                           autoFocus
                                           value={password}
                                           onChange={updatePassword}/>
                                    {viewPassword ? <VisibilityOff style={{
                                        position: 'absolute',
                                        top: '60%',
                                        right: '10px',
                                        transform: 'translateY(-60%)',
                                        cursor: 'pointer'}}
                                        onClick={togglePasswordVisibility}/> :
                                    <Visibility style={{
                                        position: 'absolute',
                                        top: '60%',
                                        right: '10px',
                                        transform: 'translateY(-60%)',
                                        cursor: 'pointer'}}
                                        onClick={togglePasswordVisibility}/>}
                                </FormControl>
                                <Link component="button" type="button" onClick={changeEmail} sx={typoStyle}>
                                    {t('changeEmail')}
                                </Link>
                            </>
                        )}
                        <Button
                            type="submit"
                            fullWidth
                            variant="contained"
                            color="primary"
                            className="Submit"
                            sx={submitStyle}
                        >
                            {passwordStep ? t('loginButton') : t('continue')}
                        </Button>
                    </form>
                    {passwordStep && (
                        <Link component={RouterLink} to="/mot-de-passe-oublie" sx={typoStyle}>
                            {t('forgotPassword')}
                        </Link>
                    )}
                </Fragment>
            </Paper>
        </Box>
    );
}